import { encodeXML } from 'entities';
import chunk from 'lodash/chunk';
import camelCase from 'lodash/camelCase';
import memoize from 'lodash/memoize';
import snakeCase from 'lodash/snakeCase';
import { coalesce } from 'src/lib/tools';
import { toQueryString } from './qs';
import { int2dt, dt2int } from './date-time-helpers';

export function js2xml (rootTag, value) {
    const xml = [];
    const p = xml.push.bind(xml);

    function createNode (tag, v) {
        const t = snakeCase(tag);
        if (v === undefined)
            p('<', t, ' isnull="true" />');

        else if (v === null)
            p('<', t, ' isnull="true" />');

        else {
            p('<', t, '>');

            if (v instanceof Array)
                v.forEach(n => createNode('item', n));

            else if (v instanceof Date)
                p(dt2int(v));

            else if (v instanceof String)
                p(encodeXML(v));

            else if (v instanceof Number)
                p(encodeXML(v.toString()));

            else if (v instanceof Boolean)
                p(v ? 'True' : 'False');

            else if (v instanceof Object)
                Object.entries(v).forEach(([k, it]) => createNode(k, it));

            else
                p(encodeXML(v.toString()));

            p('</', t, '>');
        }
    }

    createNode(rootTag, value);
    return xml.join('');
}

export function xml2js (xml) {
    const cc = memoize(camelCase);

    function extract (node) {
        if (node.getAttribute('isnull') !== null) return null;

        const primitive = coalesce(node.getAttribute('primitive'), '').toLowerCase();
        // const type = coalesce(node.getAttribute("type"), "").toLowerCase();

        switch (primitive) {
            case 'complex':
                return Array.from(node.children).reduce((a, n) => {
                    a[cc(n.tagName)] = extract(n);
                    return a;
                }, {});
            case 'array':
                return Array.from(node.children).map(extract);
            case 'datetime':
                return int2dt(Number(node.textContent).valueOf());
            case 'enum':
                return cc(coalesce(node.textContent, ''));
            case 'guid':
                return coalesce(node.textContent, '');
            case 'string':
                return coalesce(node.textContent, '');
            case 'binary':
                return coalesce(node.textContent, '');
            case 'bool':
                return node.textContent === 'True';
            case 'byte':
                return Number(node.textContent).valueOf();
            case 'sbyte':
                return Number(node.textContent).valueOf();
            case 'ushort':
                return Number(node.textContent).valueOf();
            case 'int':
                return Number(node.textContent).valueOf();
            case 'short':
                return Number(node.textContent).valueOf();
            case 'uint':
                return Number(node.textContent).valueOf();
            case 'long':
                return Number(node.textContent).valueOf();
            case 'ulong':
                return Number(node.textContent).valueOf();
            case 'float':
                return Number(node.textContent).valueOf();
            case 'decimal':
                return Number(node.textContent).valueOf();
            case 'timespan':
                return Number(node.textContent).valueOf();
            case 'xml':
                return node.textContent;
            default:
                return Array.from(node.children).reduce((a, n) => {
                    a[cc(n.tagName)] = extract(n);
                    return a;
                }, {});
        }
    }

    // const xml = parser.parseFromString(txt, "application/xml").children[0];
    const res = extract(xml);
    return res;
}

export class APIError extends Error {
    constructor (code, message) {
        super(`${code} - ${message}`);
        this.code = code;
    }
}

function invokeCore (sessionId, module, section, method, parameters, timeout = 300000) {
    return new Promise((resolve, reject) => {
        const uri = sessionId ?
            `${process.env.API_URL}?format=xml&method=${module}.${section}.${method}&idsession=${sessionId}` :
            `${process.env.API_URL}?format=xml&method=${module}.${section}.${method}`;

        const xmlparamettres = js2xml('paramettres', parameters);

        const xhr = new XMLHttpRequest();
        try {
            xhr.timeout = timeout;
        } catch (e) { }
        try {
            xhr.ontimeout = () => reject(new Error('TIMEOUT'));
        } catch (e) { }
        try {
            xhr.onerror = () => reject(new Error('ERROR'));
        } catch (e) { }
        try {
            xhr.onabort = () => reject(new Error('ABORT'));
        } catch (e) { }
        xhr.onreadystatechange = () => {
            switch (xhr.readyState) {
                case 0:
                case 1:
                case 2:
                case 3:
                    break;

                case 4:
                    if (xhr.status === 200 || xhr.status === 0) {
                        if (xhr.responseXML === null) {
                            reject(new Error('Erreur inconnue'));
                        }
                        const { status, errorCode, errorMessage, result } = xml2js(xhr.responseXML.firstChild);

                        switch (status) {
                            case 'ok':
                                resolve(result);
                                break;
                            case undefined:
                            case null:
                                reject(new Error('Erreur inconnue'));
                                break;
                            default:
                                reject(new APIError(errorCode, errorMessage));
                                break;
                        }
                    }
                    break;
                default:
                    break;
            }
        };


        xhr.open('POST', uri, true);
        xhr.setRequestHeader('Content-Type', 'application/xml; charset=utf-8');
        xhr.send(xmlparamettres);
    });
}

async function invokeHelperTabularData (page, parameters) {
    const response = await fetch(`${process.env.HELPERS_URL}${page}?${toQueryString(parameters)}`);
    const txt = await response.text();
    const data = txt.split('\u001E').map(l => l.split('\u001F'));
    return data;
}

export function Client (sessionId) {
    const invoke = (module, section, method, parameters, timeout = 300000) => invokeCore(sessionId, module, section, method, parameters, timeout);

    return {
        server: {
            session: {
                open_session: (application_id, host_id) => invoke('server', 'session', 'open_session', { application_id, host_id }),
                connect_user: (login, password, version) => invoke('server', 'session', 'connect_user', { login, password, version }),
                disconnect_user: () => invoke('server', 'session', 'disconnect_user', {}),
                close_session: () => invoke('server', 'session', 'close_session', {}),
                get_current: () => invoke('server', 'session', 'get_current', {}, 5000),
            },
            reflexion: {
                get_api_reflexion: () => invoke('server', 'reflexion', 'get_api_reflexion', {}),
            },
        },
        infrastructure: {
            account: {
                get_infos: account_id => invoke('infrastructure', 'account', 'get_infos', { account_id }),
                enum_hierarchy: account_id => invoke('infrastructure', 'account', 'enum_hierarchy', { account_id }),
                find: filter => invoke('infrastructure', 'account', 'find', { filter }),
                set_infos: (account_id, info_company, info_address, info_city, info_zipcode, info_country, info_contact_title, info_contact_firstname, info_contact_lastname, info_phone, info_fax, info_email, info_activity, info_vat_number, info_www, techemail) => invoke('infrastructure', 'account', 'set_infos', { account_id, info_company, info_address, info_city, info_zipcode, info_country, info_contact_title, info_contact_firstname, info_contact_lastname, info_phone, info_fax, info_email, info_activity, info_vat_number, info_www, techemail }),
                set_password: (account_id, newpassword) => invoke('infrastructure', 'account', 'set_password', { account_id, newpassword }),
            },
            countries: {
                get_all: () => invoke('infrastructure', 'countries', 'get_all', {}),
            },
        },
        fax: {
            campaign: {
                enum_by_state: (account_ids, campaign_states) => invoke('fax', 'campaign', 'enum_by_state', { account_ids, campaign_states }),
                get_infos: campaign_id => invoke('fax', 'campaign', 'get_infos', { campaign_id }),
                enum_plannings: campaign_ids => invoke('fax', 'campaign', 'enum_plannings', { campaign_id: campaign_ids }),
                get_document: (campaign_id, return_format = 'Png') => invoke('fax', 'campaign', 'get_document', { campaign_id, return_format }),
                set_infos: (
                    campaign_id, campaign_name, campaign_deadline, campaign_max_delivrered,
                    campaign_max_delivrered_per_day, campaign_max_delivrered_per_hour,
                    campaign_planning_start_hour, campaign_planning_stop_hour,
                    campaign_planning_allow_monday, campaign_planning_allow_tuesday,
                    campaign_planning_allow_wednesday, campaign_planning_allow_thursday,
                    campaign_planning_allow_friday, campaign_planning_allow_saturday,
                    campaign_planning_allow_sunday, id_expeditor, sender_id,
                ) => invoke('fax', 'campaign', 'set_infos', {
                    campaign_id,
                    campaign_name,
                    campaign_deadline,
                    campaign_max_delivrered,
                    campaign_max_delivrered_per_day,
                    campaign_max_delivrered_per_hour,
                    campaign_planning_start_hour,
                    campaign_planning_stop_hour,
                    campaign_planning_allow_monday,
                    campaign_planning_allow_tuesday,
                    campaign_planning_allow_wednesday,
                    campaign_planning_allow_thursday,
                    campaign_planning_allow_friday,
                    campaign_planning_allow_saturday,
                    campaign_planning_allow_sunday,
                    id_expeditor,
                    sender_id,
                }),
                duplicate: (campaign_id, campaign_name, duplicate_document, duplicate_recipients) => invoke('fax', 'campaign', 'duplicate', { campaign_id, campaign_name, duplicate_document, duplicate_recipients }),
                enum_tests_reports: campaign_id => invoke('fax', 'campaign', 'enum_tests_reports', { campaign_id }),
                send_test: (campaign_id, recipient) => invoke('fax', 'campaign', 'send_test', { campaign_id, recipient }),
                set_priority: (campaign_id, priority) => invoke('fax', 'campaign', 'set_priority', { campaign_id, priority }),
                set_document: (campaign_id, document) => invoke('fax', 'campaign', 'set_document', { campaign_id, document }),
                sleep: campaign_id => invoke('fax', 'campaign', 'sleep', { campaign_id }),
                resume: campaign_id => invoke('fax', 'campaign', 'resume', { campaign_id }),
                skip_current_planning: campaign_id => invoke('fax', 'campaign', 'skip_current_planning', { campaign_id }),
                archive: (campaign_id, archive_date, billing_date) => invoke('fax', 'campaign', 'archive', { campaign_id, archive_date, billing_date }),
                finish: campaign_id => invoke('fax', 'campaign', 'finish', { campaign_id }),
                delete_new: campaign_id => invoke('fax', 'campaign', 'delete_new', { campaign_id }),
                delete: campaign_id => invoke('fax', 'campaign', 'delete', { campaign_id }),
                set_owner: (campaign_id, account_id) => invoke('fax', 'campaign', 'set_owner', { campaign_id, account_id }),
                set_planning: (campaign_id, plannings) => invoke('fax', 'campaign', 'set_planning', { campaign_id, plannings }),
                add_planning: (campaign_id, plan_date, plan_tries) => invoke('fax', 'campaign', 'add_planning', { campaign_id, plan_date, plan_tries }),
                // enum_reports: (campaign_id) => invoke('fax', 'campaign', 'enum_reports', { campaign_id }),
                enum_reports: async campaignId => {
                    if (!campaignId) return null;
                    const response = await invokeHelperTabularData('fax-report.ashx', { sessionId, campaignId });
                    const data = response.map(([target, country, sendDate, state, detail, smartResponse, pages, ...cols]) => ({ target, country, sendDate: int2dt(Number(sendDate).valueOf()), state, detail, smartResponse, pages, cols }));
                    return data;
                },
                enum_recipients_by_countries_statistics: campaign_id => invoke('fax', 'campaign', 'enum_recipients_by_countries_statistics', { campaign_id }),
                set_allowedcountries: (campaign_id, countries_ids) => invoke('fax', 'campaign', 'set_allowedcountries', { campaign_id, countries_ids }),
                delete_targets_by_countries: (campaign_id, ids_countries) => invoke('fax', 'campaign', 'delete_targets_by_countries', { campaign_id, ids_countries }),
                accept_laws: campaign_id => invoke('fax', 'campaign', 'accept_laws', { campaign_id }),
                new: (account_id, campaign_name) => invoke('fax', 'campaign', 'new', { account_id, campaign_name }),
                get_test_documents: (campaign_id, return_format = 'Png') => invoke('fax', 'campaign', 'get_test_documents', { campaign_id, return_format }),
                set_test_documents: (campaign_id, campaign_test_document_1, campaign_test_document_2, campaign_test_document_3) => invoke('fax', 'campaign', 'set_test_documents', { campaign_id, campaign_test_document_1, campaign_test_document_2, campaign_test_document_3 }),
                get_random_customization: campaign_id => invoke('fax', 'campaign', 'get_random_customization', { campaign_id }),
                set_recipients_from_file: async (campaign_id, recipients, name_column_0, name_column_1, name_column_2, name_column_3, name_column_4, name_column_5, name_column_6, name_column_7, name_column_8, name_column_9, name_column_10, name_column_11, name_column_12, name_column_13, name_column_14, name_column_15, name_column_16, name_column_17, name_column_18, name_column_19, name_column_20, name_column_21, name_column_22, name_column_23, name_column_24, name_column_25, name_column_26, name_column_27, name_column_28, name_column_29, name_column_30, name_column_31, name_column_32, name_column_33, name_column_34, name_column_35, name_column_36, name_column_37, name_column_38, name_column_39, show_column_0, show_column_1, show_column_2, show_column_3, show_column_4, show_column_5, show_column_6, show_column_7, show_column_8, show_column_9, show_column_10, show_column_11, show_column_12, show_column_13, show_column_14, show_column_15, show_column_16, show_column_17, show_column_18, show_column_19, show_column_20, show_column_21, show_column_22, show_column_23, show_column_24, show_column_25, show_column_26, show_column_27, show_column_28, show_column_29, show_column_30, show_column_31, show_column_32, show_column_33, show_column_34, show_column_35, show_column_36, show_column_37, show_column_38, show_column_39, in_unique_filter_0, in_unique_filter_1, in_unique_filter_2, in_unique_filter_3, in_unique_filter_4, in_unique_filter_5, in_unique_filter_6, in_unique_filter_7, in_unique_filter_8, in_unique_filter_9, in_unique_filter_10, in_unique_filter_11, in_unique_filter_12, in_unique_filter_13, in_unique_filter_14, in_unique_filter_15, in_unique_filter_16, in_unique_filter_17, in_unique_filter_18, in_unique_filter_19, in_unique_filter_20, in_unique_filter_21, in_unique_filter_22, in_unique_filter_23, in_unique_filter_24, in_unique_filter_25, in_unique_filter_26, in_unique_filter_27, in_unique_filter_28, in_unique_filter_29, in_unique_filter_30, in_unique_filter_31, in_unique_filter_32, in_unique_filter_33, in_unique_filter_34, in_unique_filter_35, in_unique_filter_36, in_unique_filter_37, in_unique_filter_38, in_unique_filter_39, in_unique_filter_recipient_number) => {
                    const operation = await invoke('fax', 'campaign', 'set_recipients_from_file_init', {});
                    for (const part of chunk(recipients, 1000)) {
                        // eslint-disable-next-line no-await-in-loop
                        await invoke('fax', 'campaign', 'set_recipients_from_file_sendpart', { operation, recipients: part });
                    }
                    return invoke('fax', 'campaign', 'set_recipients_from_file_finalize', { operation, campaign_id, name_column_0, name_column_1, name_column_2, name_column_3, name_column_4, name_column_5, name_column_6, name_column_7, name_column_8, name_column_9, name_column_10, name_column_11, name_column_12, name_column_13, name_column_14, name_column_15, name_column_16, name_column_17, name_column_18, name_column_19, name_column_20, name_column_21, name_column_22, name_column_23, name_column_24, name_column_25, name_column_26, name_column_27, name_column_28, name_column_29, name_column_30, name_column_31, name_column_32, name_column_33, name_column_34, name_column_35, name_column_36, name_column_37, name_column_38, name_column_39, show_column_0, show_column_1, show_column_2, show_column_3, show_column_4, show_column_5, show_column_6, show_column_7, show_column_8, show_column_9, show_column_10, show_column_11, show_column_12, show_column_13, show_column_14, show_column_15, show_column_16, show_column_17, show_column_18, show_column_19, show_column_20, show_column_21, show_column_22, show_column_23, show_column_24, show_column_25, show_column_26, show_column_27, show_column_28, show_column_29, show_column_30, show_column_31, show_column_32, show_column_33, show_column_34, show_column_35, show_column_36, show_column_37, show_column_38, show_column_39, in_unique_filter_0, in_unique_filter_1, in_unique_filter_2, in_unique_filter_3, in_unique_filter_4, in_unique_filter_5, in_unique_filter_6, in_unique_filter_7, in_unique_filter_8, in_unique_filter_9, in_unique_filter_10, in_unique_filter_11, in_unique_filter_12, in_unique_filter_13, in_unique_filter_14, in_unique_filter_15, in_unique_filter_16, in_unique_filter_17, in_unique_filter_18, in_unique_filter_19, in_unique_filter_20, in_unique_filter_21, in_unique_filter_22, in_unique_filter_23, in_unique_filter_24, in_unique_filter_25, in_unique_filter_26, in_unique_filter_27, in_unique_filter_28, in_unique_filter_29, in_unique_filter_30, in_unique_filter_31, in_unique_filter_32, in_unique_filter_33, in_unique_filter_34, in_unique_filter_35, in_unique_filter_36, in_unique_filter_37, in_unique_filter_38, in_unique_filter_39, in_unique_filter_recipient_number });
                },
                // enum_recipients: campaign_id => invoke('fax', 'campaign', 'enum_recipients', { campaign_id }),
                enum_recipients: async campaignId => {
                    if (!campaignId) return null;
                    const response = await invokeHelperTabularData('fax-recipients.ashx', { sessionId, campaignId });
                    const data = response.map(([id, target, ...values]) => ({ id: Number(id), target, values }));
                    return data;
                },
                delete_targets_in_system_redlist: campaign_id => invoke('fax', 'campaign', 'delete_targets_in_system_redlist', { campaign_id }),
                delete_targets_in_noads_redlist: campaign_id => invoke('fax', 'campaign', 'delete_targets_in_noads_redlist', { campaign_id }),
                delete_targets_in_personal_redlist: campaign_id => invoke('fax', 'campaign', 'delete_targets_in_personal_redlist', { campaign_id }),
                set_redlist_filters: (campaign_id, personnal_redlist, noad_redlist) => invoke('fax', 'campaign', 'set_redlist_filters', { campaign_id, personnal_redlist, noad_redlist }),
                delete_duplicated_targets: campaign_id => invoke('fax', 'campaign', 'delete_duplicated_targets', { campaign_id }),
                delete_invalid_targets: campaign_id => invoke('fax', 'campaign', 'delete_invalid_targets', { campaign_id }),
                set_data_rental_filter: (campaign_id, xml_query) => invoke('fax', 'campaign', 'set_data_rental_filter', { campaign_id, xml_query }),
            },
            account: {
                add_to_personnal_redlist: (account_id, recipient_number) => invoke('fax', 'account', 'add_to_personnal_redlist', { account_id, recipient_number }),
                // enum_personnal_redlist: (account_id) => invoke('fax', 'account', 'enum_personnal_redlist', { account_id }),
                enum_personnal_redlist: async accountId => {
                    if (!accountId) return null;
                    const response = await invokeHelperTabularData('fax-blacklist.ashx', { sessionId, accountId });
                    return response.map(([recipient, comment]) => ({ recipient, comment }));
                },
                set_personnal_redlist: async (account_id, list) => {
                    const operation = await invoke('fax', 'account', 'init_set_personnal_redlist', {});
                    for (const part of chunk(list, 1000)) {
                        // eslint-disable-next-line no-await-in-loop
                        await invoke('fax', 'account', 'sendpart_set_personnal_redlist', { operation, liste: part });
                    }
                    return invoke('fax', 'account', 'finalize_set_personnal_redlist', { operation, id_compte: account_id });
                },
                open_billing_session: account_id => invoke('fax', 'account', 'openbillingsession', { id_compte: account_id }),
                enum_credit_balances: account_ids => invoke('fax', 'account', 'enum_credit_balances', { account_ids }),
                enum_credits: account_ids => invoke('fax', 'account', 'enum_credits', { account_ids }),
                set_credit_balance: (account_id, credit_id, variation) => invoke('fax', 'account', 'set_credit_balance', { account_id, credit_id, variation }),
                enum_credit_balance_history: (account_id, history_start_date, history_end_date) => invoke('fax', 'account', 'enum_credit_balance_history', { account_id, history_start_date, history_end_date }),
            },
            tools: {
                convert_doc_to_fax: (file_suffix, document, return_format = 'Png') => invoke('fax', 'tools', 'convert_doc_to_fax', { file_suffix, document, return_format }),
                get_fonts_available: async () => {
                    const response = await invokeHelperTabularData('fonts-list.ashx', {});
                    return response.map(i => i[0]);
                },
            },
        },
    };
}